import React from 'react'
import { X, Edit, Award, Calendar, Package } from 'lucide-react'
import { Modal, Button } from '@components/common'
import './CertificateView.css'

/**
 * Icon mapping for certificate icons (same as CertificateList)
 */
import {
	Award as AwardIcon,
	Shield,
	CheckCircle,
	Lock,
	UserCheck,
	UserLock,
	Leaf,
	Heart,
	Hospital,
	CreditCard,
	Flag,
	CloudUpload,
	Cloud,
	Pill,
	RefreshCw,
} from 'lucide-react'

const iconMap = {
	award: AwardIcon,
	'shield-alt': Shield,
	'check-circle': CheckCircle,
	lock: Lock,
	'user-shield': UserCheck,
	'user-lock': UserLock,
	leaf: Leaf,
	heartbeat: Heart,
	hospital: Hospital,
	'credit-card': CreditCard,
	'flag-usa': Flag,
	'cloud-upload-alt': CloudUpload,
	cloud: Cloud,
	'cloud-lock': Cloud,
	pills: Pill,
	'sync-alt': RefreshCw,
	'file-signature': AwardIcon,
	'hard-hat': Shield,
}

/**
 * Get icon component for certificate
 */
const getCertificateIcon = (iconName) => {
	const IconComponent = iconMap[iconName] || AwardIcon
	return IconComponent
}

/**
 * CertificateView Component
 *
 * Read-only view of certificate details with organized sections
 *
 * Features:
 * - Comprehensive certificate information display
 * - Formatted values (dates)
 * - Status badges
 * - Quick edit action
 * - Accessible and responsive
 *
 * @param {Object} certificateData - Certificate data to display
 * @param {Function} onClose - Close modal callback
 * @param {Function} onEdit - Edit action callback (optional)
 */
const CertificateView = ({ certificateData, onClose, onEdit }) => {
	if (!certificateData) return null

	/**
	 * Format date
	 */
	const formatDate = (dateString) => {
		if (!dateString) return '-'
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		})
	}

	/**
	 * Get status badge class
	 */
	const getStatusClass = (status) => {
		return status === 'active' ? 'certificate-view__badge--success' : 'certificate-view__badge--secondary'
	}

	const CertIcon = getCertificateIcon(certificateData.icon)

	const footer = (
		<>
			{onEdit && !certificateData.deleted_at && certificateData.status !== 'archived' && (
				<Button variant="primary" onClick={onEdit}>
					<Edit size={18} />
					<span>Edit Certificate</span>
				</Button>
			)}
			<Button variant="secondary" onClick={onClose}>
				Close
			</Button>
		</>
	)

	return (
		<Modal isOpen={true} onClose={onClose} title="Certificate Details" size="large" footer={footer}>
			<div className="certificate-view">
				{/* Header Section */}
				<div className="certificate-view__header">
					<div className="certificate-view__header-content">
						<div className="certificate-view__icon">
							<CertIcon size={32} />
						</div>
						<div>
							<h2 className="certificate-view__title">{certificateData.title}</h2>
							<p className="certificate-view__slug">{certificateData.slug}</p>
						</div>
					</div>
					<div className={`certificate-view__badge ${getStatusClass(certificateData.status)}`}>
						{certificateData.status}
					</div>
				</div>

				{/* Description */}
				{certificateData.description && (
					<div className="certificate-view__section">
						<p className="certificate-view__description">{certificateData.description}</p>
					</div>
				)}

				{/* Certificate Information */}
				<div className="certificate-view__section">
					<h3 className="certificate-view__section-title">Certificate Information</h3>
					<div className="certificate-view__grid">
						<div className="certificate-view__item">
							<div className="certificate-view__item-icon">
								<Award size={20} />
							</div>
							<div className="certificate-view__item-content">
								<span className="certificate-view__item-label">Icon</span>
								<span className="certificate-view__item-value">
									<CertIcon size={16} /> {certificateData.icon}
								</span>
							</div>
						</div>

						<div className="certificate-view__item">
							<div className="certificate-view__item-icon">
								<Package size={20} />
							</div>
							<div className="certificate-view__item-content">
								<span className="certificate-view__item-label">Used in Packages</span>
								<span className="certificate-view__item-value">
									{certificateData.packages_count || 0} package(s)
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Metadata */}
				<div className="certificate-view__section">
					<h3 className="certificate-view__section-title">Metadata</h3>
					<div className="certificate-view__metadata">
						<div className="certificate-view__metadata-item">
							<span className="certificate-view__metadata-label">Created</span>
							<span className="certificate-view__metadata-value">
								{formatDate(certificateData.created_at)}
							</span>
						</div>
						<div className="certificate-view__metadata-item">
							<span className="certificate-view__metadata-label">Last Updated</span>
							<span className="certificate-view__metadata-value">
								{formatDate(certificateData.updated_at)}
							</span>
						</div>
						{certificateData.deleted_at && (
							<div className="certificate-view__metadata-item">
								<span className="certificate-view__metadata-label">Archived</span>
								<span className="certificate-view__metadata-value">
									{formatDate(certificateData.deleted_at)}
								</span>
							</div>
						)}
					</div>
				</div>
			</div>
		</Modal>
	)
}

export default CertificateView
